'use client'
import { useNextSanityImage } from 'next-sanity-image'
import NextImage, { ImageProps } from 'next/image'

import { Locale, useLocale } from 'next-intl'
import { CSSProperties } from 'react'
import { getClient } from '@/sanity/client'
import { local } from '@/local'
import { SanityImageHotspot } from '@sanity/image-url/lib/types/types'
import { Image } from '@/sanity/types'

export type SanityImageData =
  Image & {
    hotspot?: SanityImageHotspot | null
  }


interface Props
  extends Omit<
    ImageProps,
    'src' | 'alt' | 'width' | 'height' | 'blurDataURL' | 'placeholder'
  > {
  image?: SanityImageData | null
  aspectRatio?: number
  fallbackAltText?: string
  quality?: number
}

export function SanityImage({
  image,
  fill = false,
  aspectRatio,
  fallbackAltText,
  quality,
  ...props
}: Props) {
  const locale = useLocale() as Locale
  const sanityImage = useNextSanityImage(
    getClient({ draftMode: false }),
    image!,
    {
      imageBuilder: (imageUrlBuilder, options) => {
        if (options.width) {
          imageUrlBuilder = imageUrlBuilder.width(options.width)
          if (aspectRatio) {
            imageUrlBuilder = imageUrlBuilder.height(
              options.width / aspectRatio,
            )
          }
        }

        imageUrlBuilder = imageUrlBuilder
          .quality(quality ?? options.quality ?? 70)
          .auto('format')

        return imageUrlBuilder
      },
    },
  )
  if (!image || !sanityImage) return null
  const { width, height, src, loader } = sanityImage

  const style = {} as CSSProperties
  if ('hotspot' in image && image.hotspot?.x && image.hotspot?.y) {
    const { x, y } = image.hotspot
    style.objectPosition = `${x * 100}% ${y * 100}%`
  } else {
    style.objectPosition = 'center'
  }

  const altText = local(locale, image.alt)

  return (
    <NextImage
      src={src}
      loader={loader}
      width={fill ? undefined : width}
      height={fill ? undefined : height}
      fill={fill}
      {...props}
      placeholder={image.asset?.metadata?.lqip ? 'blur' : undefined}
      alt={altText?.trim() ? altText : (fallbackAltText ?? '')}
      blurDataURL={image.asset?.metadata?.lqip ?? undefined}
    />
  )
}
